import React, { useState, useMemo } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";

// ───────── Helpers formattazione ─────────

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function formatCurrency0(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);
}

// Converte una stringa in formato italiano ("1.200,50") in numero.
function parseToNumber(str) {
  if (str === null || str === undefined) return 0;
  const cleaned = String(str).replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
}

// ───────── Calcolo IRPEF a scaglioni (regime ordinario) ─────────
// Scaglioni semplificati: 0-28k 23%, 28k-50k 35%, oltre 50k 43%.
const SCAGLIONI_IRPEF = [
  { limite: 28000, aliquota: 0.23 },
  { limite: 50000, aliquota: 0.35 },
  { limite: Infinity, aliquota: 0.43 },
];

function calcolaIrpefScaglioni(base) {
  const imponibile = Math.max(0, Number(base) || 0);
  let imposta = 0;
  let precedente = 0;
  for (const scaglione of SCAGLIONI_IRPEF) {
    if (imponibile <= precedente) break;
    const quota = Math.min(imponibile, scaglione.limite) - precedente;
    imposta += quota * scaglione.aliquota;
    precedente = scaglione.limite;
  }
  return imposta;
}

const ALIQUOTA_ADDIZIONALI_ORDINARIO = 0.02; // stima 2% sull'imponibile

// ───────── Calcolo scenario Forfettario ─────────
function calcolaForfettario({ incassi, coefficiente, aliquotaImposta, inpsRatePct }) {
  const imponibile = incassi * coefficiente;
  const inps = imponibile * (inpsRatePct / 100);
  const baseImposta = Math.max(0, imponibile - inps);
  const tasse = baseImposta * (aliquotaImposta / 100);
  const netto = incassi - inps - tasse;
  return { imponibile, inps, tasse, netto };
}

// ───────── Calcolo scenario Ordinario (semplificato) ─────────
function calcolaOrdinario({ fatturato, costiPct, inpsRatePct }) {
  const costi = fatturato * (costiPct / 100);
  const imponibile = Math.max(0, fatturato - costi);
  const inps = imponibile * (inpsRatePct / 100);
  const baseIrpef = Math.max(0, imponibile - inps); // INPS deducibile
  const irpef = calcolaIrpefScaglioni(baseIrpef);
  const addizionali = imponibile * ALIQUOTA_ADDIZIONALI_ORDINARIO;
  const netto = fatturato - costi - inps - irpef - addizionali;
  return { costi, imponibile, inps, irpef, addizionali, tasse: irpef + addizionali, netto };
}

// Ricerca (a passi + rifinitura binaria) del fatturato di break-even tra i due regimi,
// mantenendo costanti coefficiente/aliquote/% costi impostati dall'utente.
function trovaBreakEven({ coefficiente, aliquotaImposta, inpsRatePct, costiPct }) {
  const nettoForf = (f) => calcolaForfettario({ incassi: f, coefficiente, aliquotaImposta, inpsRatePct }).netto;
  const nettoOrd = (f) => calcolaOrdinario({ fatturato: f, costiPct, inpsRatePct }).netto;

  const STEP = 500;
  const MAX_F = 300000;
  let prevDiff = nettoForf(STEP) - nettoOrd(STEP);
  const segnoIniziale = Math.sign(prevDiff);

  for (let f = STEP * 2; f <= MAX_F; f += STEP) {
    const diff = nettoForf(f) - nettoOrd(f);
    if (Math.sign(diff) !== segnoIniziale && Math.sign(diff) !== 0) {
      let lo = f - STEP;
      let hi = f;
      for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        const dMid = nettoForf(mid) - nettoOrd(mid);
        if (Math.sign(dMid) === segnoIniziale) lo = mid;
        else hi = mid;
      }
      return { soglia: (lo + hi) / 2, forfettarioConvieneSotto: segnoIniziale > 0, trovato: true };
    }
    prevDiff = diff;
  }
  // Nessun incrocio trovato nel range analizzato: un regime vince sempre.
  return { soglia: null, forfettarioConvieneSotto: segnoIniziale > 0, trovato: false };
}

export default function ForfettarioVsOrdinarioSimulator({
  incassi = 0,
  coefficiente = 0.78,
  aliquotaImposta = 5,
  inpsRate = 26.07,
  darkMode = false,
}) {
  const [incassiInput, setIncassiInput] = useState(
    incassi ? String(Math.round(incassi)) : ""
  );
  const [coefficientePct, setCoefficientePct] = useState(
    String(Math.round((coefficiente || 0.78) * 100))
  );
  const [aliquotaSel, setAliquotaSel] = useState(aliquotaImposta === 15 ? 15 : 5);
  // Formato italiano (virgola) atteso da parseToNumber: String(26.07) darebbe "26.07",
  // e parseToNumber rimuove i punti trattandoli come migliaia → 2607. Con la virgola
  // "26,07" viene interpretato correttamente come 26,07.
  const [inpsRatePctInput, setInpsRatePctInput] = useState(
    String(inpsRate ?? 26.07).replace(".", ",")
  );
  const [costiPctInput, setCostiPctInput] = useState("30");

  const fatturato = parseToNumber(incassiInput);
  const coefficienteDec = Math.min(1, Math.max(0, parseToNumber(coefficientePct) / 100));
  const inpsRatePct = Math.max(0, parseToNumber(inpsRatePctInput));
  const costiPct = Math.min(100, Math.max(0, parseToNumber(costiPctInput)));

  const forfettario = useMemo(
    () =>
      calcolaForfettario({
        incassi: fatturato,
        coefficiente: coefficienteDec,
        aliquotaImposta: aliquotaSel,
        inpsRatePct,
      }),
    [fatturato, coefficienteDec, aliquotaSel, inpsRatePct]
  );

  const ordinario = useMemo(
    () => calcolaOrdinario({ fatturato, costiPct, inpsRatePct }),
    [fatturato, costiPct, inpsRatePct]
  );

  const breakEven = useMemo(
    () =>
      trovaBreakEven({
        coefficiente: coefficienteDec,
        aliquotaImposta: aliquotaSel,
        inpsRatePct,
        costiPct,
      }),
    [coefficienteDec, aliquotaSel, inpsRatePct, costiPct]
  );

  const haDati = fatturato > 0;
  const differenza = forfettario.netto - ordinario.netto;
  const forfettarioConviene = differenza >= 0;

  const inputClass = `w-full pl-3 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-400 focus:border-forfy-400 ${
    darkMode
      ? "border-neutral-700 bg-neutral-800 text-white"
      : "border-neutral-300 bg-white text-neutral-900"
  }`;
  const labelClass = `text-xs font-medium mb-1 block ${
    darkMode ? "text-neutral-400" : "text-neutral-600"
  }`;

  return (
    <div className="space-y-6">
      {/* Input */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Incassi / fatturato annuo</label>
          <div className="relative">
            <span
              className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              €
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={incassiInput}
              onChange={(e) => setIncassiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
              placeholder="Es. 40000"
              className={`${inputClass} pl-8`}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Coefficiente redditività</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={coefficientePct}
              onChange={(e) => setCoefficientePct(e.target.value.replace(/[^0-9,.]/g, ""))}
              placeholder="78"
              className={`${inputClass} pr-8`}
            />
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              %
            </span>
          </div>
        </div>

        <div>
          <label className={labelClass}>Aliquota imposta forfettario</label>
          <div className="flex gap-2">
            {[5, 15].map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setAliquotaSel(val)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  aliquotaSel === val
                    ? "bg-forfy-500 border-forfy-500 text-white"
                    : darkMode
                    ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                    : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {val}%
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={labelClass}>Aliquota INPS</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={inpsRatePctInput}
              onChange={(e) => setInpsRatePctInput(e.target.value.replace(/[^0-9,.]/g, ""))}
              placeholder="26,07"
              className={`${inputClass} pr-8`}
            />
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              %
            </span>
          </div>
        </div>

        <div>
          <label className={labelClass}>Costi deducibili (ordinario)</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={costiPctInput}
              onChange={(e) => setCostiPctInput(e.target.value.replace(/[^0-9,.]/g, ""))}
              placeholder="30"
              className={`${inputClass} pr-8`}
            />
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              %
            </span>
          </div>
        </div>
      </div>

      {!haDati && (
        <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
          Inserisci un importo di incassi/fatturato per vedere il confronto.
        </p>
      )}

      {haDati && (
        <>
          {/* Colonne confronto */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Forfettario */}
            <div
              className={`rounded-xl p-4 border ${
                darkMode
                  ? "bg-forfy-500/10 border-forfy-500/30"
                  : "bg-forfy-50 border-forfy-200"
              }`}
            >
              <h4
                className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                  darkMode ? "text-forfy-300" : "text-forfy-700"
                }`}
              >
                Forfettario
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    Imponibile
                  </span>
                  <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(forfettario.imponibile)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    INPS
                  </span>
                  <span className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    -{formatCurrency(forfettario.inps)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    Imposta {aliquotaSel}%
                  </span>
                  <span className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    -{formatCurrency(forfettario.tasse)}
                  </span>
                </div>
                <div
                  className={`border-t pt-2 mt-2 ${
                    darkMode ? "border-forfy-500/30" : "border-forfy-200"
                  }`}
                >
                  <div className="flex justify-between">
                    <span className={`text-sm font-semibold ${darkMode ? "text-forfy-300" : "text-forfy-700"}`}>
                      Netto
                    </span>
                    <span className={`text-sm font-bold ${darkMode ? "text-forfy-300" : "text-forfy-700"}`}>
                      {formatCurrency(forfettario.netto)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Ordinario */}
            <div
              className={`rounded-xl p-4 border ${
                darkMode ? "bg-indigo-900/20 border-indigo-800" : "bg-indigo-50 border-indigo-200"
              }`}
            >
              <h4
                className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                  darkMode ? "text-indigo-400" : "text-indigo-700"
                }`}
              >
                Ordinario
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    Costi ({costiPct}%)
                  </span>
                  <span className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    -{formatCurrency(ordinario.costi)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    Imponibile
                  </span>
                  <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(ordinario.imponibile)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    INPS
                  </span>
                  <span className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    -{formatCurrency(ordinario.inps)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
                    IRPEF + addizionali
                  </span>
                  <span className={`text-sm ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    -{formatCurrency(ordinario.tasse)}
                  </span>
                </div>
                <div
                  className={`border-t pt-2 mt-2 ${darkMode ? "border-indigo-800" : "border-indigo-200"}`}
                >
                  <div className="flex justify-between">
                    <span className={`text-sm font-semibold ${darkMode ? "text-indigo-400" : "text-indigo-700"}`}>
                      Netto
                    </span>
                    <span className={`text-sm font-bold ${darkMode ? "text-indigo-400" : "text-indigo-700"}`}>
                      {formatCurrency(ordinario.netto)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Barre di confronto semplice */}
          <div
            className={`rounded-xl border p-4 ${
              darkMode ? "border-neutral-800 bg-neutral-800/30" : "border-neutral-200 bg-neutral-50"
            }`}
          >
            {(() => {
              const maxNetto = Math.max(forfettario.netto, ordinario.netto, 1);
              const barForf = Math.max(0, (forfettario.netto / maxNetto) * 100);
              const barOrd = Math.max(0, (ordinario.netto / maxNetto) * 100);
              return (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={darkMode ? "text-forfy-300" : "text-forfy-700"}>Netto forfettario</span>
                      <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                        {formatCurrency0(forfettario.netto)}
                      </span>
                    </div>
                    <div className={`h-2.5 rounded-full overflow-hidden ${darkMode ? "bg-neutral-700" : "bg-neutral-200"}`}>
                      <div className="h-full bg-forfy-500 rounded-full transition-all" style={{ width: `${barForf}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={darkMode ? "text-indigo-400" : "text-indigo-700"}>Netto ordinario</span>
                      <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                        {formatCurrency0(ordinario.netto)}
                      </span>
                    </div>
                    <div className={`h-2.5 rounded-full overflow-hidden ${darkMode ? "bg-neutral-700" : "bg-neutral-200"}`}>
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${barOrd}%` }} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Frase di sintesi */}
          <div className={`rounded-xl p-4 ${darkMode ? "bg-neutral-800" : "bg-neutral-50"}`}>
            <p className={`text-sm ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
              Con <strong>{formatCurrency(fatturato)}</strong> in forfettario, il tuo netto è{" "}
              <strong className={darkMode ? "text-forfy-300" : "text-forfy-700"}>
                {formatCurrency(forfettario.netto)}
              </strong>
              . In regime ordinario, a parità di fatturato, il netto stimato è{" "}
              <strong className={darkMode ? "text-indigo-400" : "text-indigo-700"}>
                {formatCurrency(ordinario.netto)}
              </strong>
              .
            </p>
            <div className="flex items-center gap-2 mt-2">
              <ArrowRight className={`w-4 h-4 flex-shrink-0 ${forfettarioConviene ? (darkMode ? "text-emerald-400" : "text-emerald-600") : (darkMode ? "text-amber-400" : "text-amber-600")}`} />
              <p className={`text-sm font-medium ${forfettarioConviene ? (darkMode ? "text-emerald-400" : "text-emerald-700") : (darkMode ? "text-amber-400" : "text-amber-700")}`}>
                {forfettarioConviene
                  ? `Il forfettario conviene: +${formatCurrency(Math.abs(differenza))} di netto rispetto all'ordinario.`
                  : `L'ordinario conviene: +${formatCurrency(Math.abs(differenza))} di netto rispetto al forfettario.`}
              </p>
            </div>
          </div>

          {/* Break-even indicativo */}
          <div
            className={`rounded-xl p-4 border ${
              darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"
            }`}
          >
            <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
              Break-even indicativo
            </h4>
            {breakEven.trovato ? (
              <p className={`text-sm ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                Con i parametri impostati,{" "}
                {breakEven.forfettarioConvieneSotto ? (
                  <>
                    il <strong>forfettario conviene sotto</strong>{" "}
                    <strong>{formatCurrency0(breakEven.soglia)}</strong> di fatturato annuo;
                    oltre questa soglia il regime ordinario diventa più conveniente.
                  </>
                ) : (
                  <>
                    l'<strong>ordinario conviene sotto</strong>{" "}
                    <strong>{formatCurrency0(breakEven.soglia)}</strong> di fatturato annuo;
                    oltre questa soglia il forfettario diventa più conveniente.
                  </>
                )}
              </p>
            ) : (
              <p className={`text-sm ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                Nell'intervallo analizzato (fino a €300.000 di fatturato), con i parametri impostati il{" "}
                <strong>{breakEven.forfettarioConvieneSotto ? "forfettario" : "regime ordinario"}</strong>{" "}
                risulta sempre più conveniente.
              </p>
            )}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div className={`flex gap-3 p-3 rounded-lg ${darkMode ? "bg-amber-900/20" : "bg-amber-50"}`}>
        <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${darkMode ? "text-amber-400" : "text-amber-600"}`} />
        <p className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-800"}`}>
          Simulazione indicativa, non consulenza fiscale. Il regime ordinario qui calcolato è
          semplificato (IRPEF a scaglioni, addizionali stimate al 2%, nessuna deduzione
          aggiuntiva). Consulta il commercialista per decisioni importanti.
        </p>
      </div>
    </div>
  );
}
